/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

/* eslint-disable promise/always-return */
/* eslint-disable promise/catch-or-return */

import { useIdentityGatewayServiceStore } from 'dxpConsoleCore-mf/State';
import React, { useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { lastValueFrom } from 'rxjs';
import { DXP_LOCAL_SESSION_KEY } from '../../Constants/App.constants';
import { getAuthService } from '../../Services/auth';
import { getConsoleService } from '../../Services/console';
import { getIdentityGatewayService } from '../../Services/identity-gateway';
import { getServiceTokens } from '../../Utilities/GetServiceTokens/GetServiceTokens';

/**
 * Upon successful login auth0 will redirect to this page.
 * The redirect url will contain authorization code to exchange it for tokens.
 * Once successfully signed in on auth0 side, will redirect back to /authorize with authorization code.
 */
export const DxpLoginAuthorize = (): JSX.Element => {
  const location = useLocation();
  const navigate = useNavigate();

  const authService = getAuthService();
  const consoleService = getConsoleService();
  const identityGatewayService = getIdentityGatewayService();

  const { updateHasSession, updateSessionIssue } = useIdentityGatewayServiceStore();

  const authorizationCode = new URLSearchParams(location.search).get(`code`) as string;
  const authorizationState = new URLSearchParams(location.search).get(`state`) as string;

  const logInHandler = useCallback((): void => {
    lastValueFrom(consoleService.logInWithAuth0())
      .then(async () => {
        updateHasSession(true);
        await getServiceTokens();

        // Check for stored session
        const localSession = localStorage.getItem(DXP_LOCAL_SESSION_KEY);

        if (localSession) {
          // Remove session
          localStorage.removeItem(DXP_LOCAL_SESSION_KEY);

          // Navigate to IdP SSO service to continue
          const ssoCallbackUrl = identityGatewayService.createSsoCallbackUrl();
          window.location.assign(`${ssoCallbackUrl}?code=${localSession}`);
        } else {
          // Stay within DXP Console if this is a DXP Login flow
          navigate(authorizationState, { replace: true });
        }
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error(error);
        updateSessionIssue(true);
      });
  }, [authorizationState, consoleService, identityGatewayService, navigate, updateHasSession, updateSessionIssue]);

  const authorizeHandler = useCallback(
    (authorizationCode: string): void => {
      lastValueFrom(
        authService.exchangeCodeWithToken({
          code: authorizationCode,
          grantType: `authorization_code`,
          redirectUri: encodeURI(window.location.origin),
        }),
      )
        .then(() => {
          return logInHandler();
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error(error);
          updateSessionIssue(true);
        });
    },
    [authService, logInHandler, updateSessionIssue],
  );

  useEffect(() => {
    authorizeHandler(authorizationCode);
  }, [authorizationCode, authorizeHandler]);

  return <></>;
};
